import {
  Component,
  ComponentInterface,
  h,
  Host,
  Element,
  State,
  Watch,
  Prop,
  Event,
  writeTask,
  Build,
  EventEmitter,
} from '@stencil/core';
import { getActiveElement } from '../../utils/active-element';
import { debounce } from '../../utils/throttle';
import type { NanoMenuCustomEvent, OptionInterface } from '../../interface';
import type { Dropdown } from '../dropdown/dropdown';

function findLabel(formCtrl: Element) {
  let foundLabel: HTMLLabelElement;

  if (formCtrl.id) {
    foundLabel = document.querySelector(`label[for='${formCtrl.id}']`);
  }
  if (!foundLabel) {
    foundLabel = formCtrl.closest('label');
  }
  return foundLabel;
}

let listIds = 0;

/**
 * `nano-datalist` a visually consistent and more flexible replacement for a native
 * [datalist](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist). Can be nested within a `nano-input`
 * or linked to any input control via the `input` prop. Is used internally within `nano-select`.
 * Options can be defined declaratively by nesting `<nano-option>` elements or imperatively using the `options` prop
 *
 * @slot - use `<nano-option>` elements for more complex html. Alternatively use the `options` prop
 * @slot list-top - shows at the top of the list when there are options present
 * @slot list-bottom - shows at the bottom of the list when there are options present
 * @slot no-result - shown when there are no results otherwise nothing will be shown
 */
@Component({
  tag: 'nano-datalist',
  styleUrl: 'datalist.scss',
  shadow: true,
})
export class DataList implements ComponentInterface {
  // Private State

  private isNanoInput = false;
  private typeToSelect = '';
  private typeToSelectTimeout: number;
  private nanoDropdown: HTMLNanoDropdownElement;
  private listBox: HTMLNanoMenuElement;
  private mo?: MutationObserver;
  private listId = `nano-datalist-${listIds++}`;
  private isFiltered = false;
  private shouldFocus = false;
  // all potential option eles. Set via slot or `options` prop.
  // Some may get hidden due to filtering
  private get allOptEles() {
    return this._allOptEles;
  }
  private set allOptEles(opts) {
    // when allOptEles are initially set (on slot change / options) -
    // add options to 'selected' and fire selected event *if*
    // value isn't set on the input
    this._allOptEles = opts;
    if (this.connectedInput?.value) return;

    opts.forEach((opt) => {
      if (opt.selected && !this.selected.includes(opt.value)) {
        this.changeInputValue(opt);
      }
    });
  }
  private _allOptEles: HTMLNanoOptionElement[] = [];

  @Element() host: HTMLNanoDatalistElement;

  // active option eles that are not hidden.
  @State() actvOptEles: HTMLNanoOptionElement[] = [];
  @State() connectedInput: HTMLInputElement | HTMLTextAreaElement;
  @State() inputLabel: HTMLLabelElement;
  @State() hasNoResult: boolean;
  @State() shouldOpen: boolean;
  @State() canOpen: boolean = true;
  @State() optionIds: string[] = [];

  constructor() {
    this.inputChange = debounce(this.inputChange.bind(this), 50);
  }

  // Public API

  /** By default, items in the list will appear 'selected' when the value in the linked input control matches.
   * However you can add other selected options via the the selected prop. */
  @Prop({ mutable: true }) selected: string[] = [];

  /** nano-dropdown config options you can pass to the nested dropdown component */
  @Prop()
  get dropDownConfig() {
    return this._dropDownConfig;
  }
  set dropDownConfig(ddc: Partial<Dropdown>) {
    this._dropDownConfig = { ...this._dropDownConfig, ...ddc };
  }
  private _dropDownConfig: Partial<Dropdown> = {
    skidding: -1,
  };

  /** Provide an option list.
   * Options can be defined declaratively by nesting `<nano-option>` elements or imperatively using this prop */
  @Prop() options?: OptionInterface[] = [];

  /** A selector to a HTMLInputElement */
  @Prop() input?: string | HTMLInputElement;

  /** Tweaks the datalist behaviour and linked control semantics and behaviour.
   * e.g. 'select' will make the linked input control `readonly`.
   */
  @Prop() type: 'select' | 'selctMulti' | 'input' = 'input';

  /** return all the active options currently within the datalist. Can be useful for validation */
  @Prop()
  get activeOptions() {
    return this.allOptEles;
  }

  /** Force datalist open */
  @Prop({ mutable: true }) open: boolean = false;

  @Watch('open')
  openWatcher() {
    writeTask(() => {
      this.nanoDropdown.open = this.open;
      this.connectedInput.setAttribute('aria-expanded', this.open.toString());
    });
  }

  /** Use this option to disable to default filtering. This is useful if filtering happens
   * externally via another method (e.g. via ajax)
   */
  @Prop() disableFilter = false;

  /** Activate / deactivate the datalist control */
  @Prop() disabled = false;

  // State change watchers

  @Watch('input')
  async watchInputChange() {
    let nanoInput;
    if ((nanoInput = this.host.closest('nano-input'))) {
      this.isNanoInput = true;
      this.connectedInput = await nanoInput.getInputElement();
    } else if (
      this.input &&
      typeof this.input === 'string' &&
      (nanoInput = document.querySelector(this.input))
    ) {
      this.isNanoInput = false;
      this.connectedInput = nanoInput;
    } else if (this.input instanceof HTMLElement) {
      this.connectedInput = this.input;
    }
  }

  @Watch('options')
  manageSlotChangeListener() {
    if (!this.host) return;

    // we're not using `options` - setup new MO
    if ((!this.options || !this.options.length) && !this.mo) {
      if (Build.isBrowser) {
        const mo = (this.mo = new MutationObserver(() =>
          this.processSlottedContent()
        ));
        mo.observe(this.host, { childList: true, subtree: true });
      }
      this.processSlottedContent();
      return;
    }

    // we're using `options` - trash current MO
    if (this.options?.length) {
      if (!!this.mo) {
        this.mo.disconnect();
        this.mo = undefined;
      }

      const previousOptions = this.host.querySelectorAll(
        '[slot="internal-opts"]'
      );

      // add imperative `options`
      writeTask(() => {
        // clear all previous
        if (previousOptions?.length)
          previousOptions.forEach((internalOpt) => internalOpt.remove());

        this.allOptEles = this.options.flatMap((option, i) => {
          if (option.value || option.label) {
            const opt = Object.assign(document.createElement('nano-option'), {
              label: option.label,
              value: option.value,
              ariaPosinset: i,
              ariaSetsize: this.options.length,
              selected: option.selected,
              id: this.listId + '-option-' + i,
              textContent: option.label ? option.label : option.value,
              slot: 'internal-opts',
            });
            this.host.append(opt);
            return opt;
          }
        });

        if (this.connectedInput?.value.length && this.type !== 'select') {
          this.inputChange();
        } else {
          this.actvOptEles = [...this.allOptEles];
        }
      });
    }
    // forceUpdate(this.host);
  }

  @Watch('type')
  @Watch('connectedInput')
  watchTypeChange() {
    if (!this.connectedInput) return;

    let dwConfig: Partial<Dropdown> = { closeOnSelect: false };
    let autocompleteType: 'list' | 'both' = 'both';
    let readonly = false;

    switch (this.type) {
      case 'input':
        dwConfig = { closeOnSelect: true };
        break;
      case 'select':
        autocompleteType = 'list';
        readonly = true;
        dwConfig = { closeOnSelect: true, placement: 'center' };
        break;
    }

    if (!this.isNanoInput) {
      dwConfig.tetherTo = this.connectedInput;
    }

    this.dropDownConfig = { ...this.dropDownConfig, ...dwConfig };
    this.connectedInput.setAttribute('aria-autocomplete', autocompleteType);
    this.connectedInput.readOnly = readonly;
  }

  @Watch('connectedInput')
  manageInputEvents(newInput?: HTMLInputElement, oldInput?: HTMLInputElement) {
    if (!!oldInput) {
      const nanoInput = oldInput.closest('nano-input');
      if (nanoInput) {
        nanoInput.removeEventListener('nanoChange', this.inputChange);
      }
      oldInput.removeEventListener('change', this.inputChange);
      oldInput.removeEventListener('click', this.inputClick);
      oldInput.removeEventListener('keydown', this.inputKeydown);
      oldInput.removeEventListener('input', this.inputChange);

      this.inputLabel = null;

      writeTask(() => {
        oldInput.removeAttribute('role');
        oldInput.removeAttribute('aria-expanded');
        oldInput.removeAttribute('aria-controls');
        oldInput.removeAttribute('aria-owns');
        oldInput.removeAttribute('aria-haspopup');
        oldInput.removeAttribute('aria-autocomplete');
        oldInput.removeAttribute('autocomplete');
      });
    }
    if (!!newInput) {
      const nanoInput = newInput.closest('nano-input');
      if (nanoInput) {
        nanoInput.addEventListener('nanoChange', this.inputChange);
      }
      newInput.addEventListener('change', this.inputChange);
      newInput.addEventListener('click', this.inputClick);
      newInput.addEventListener('keydown', this.inputKeydown);
      newInput.addEventListener('input', this.inputChange);
      this.listId = this.host.id || this.listId;

      this.inputLabel = newInput?.labels?.item(0) || findLabel(newInput);

      writeTask(() => {
        this.host.id = this.listId;
        newInput.setAttribute('role', 'combobox');
        newInput.setAttribute('aria-expanded', 'false');
        newInput.setAttribute('aria-controls', this.listId);
        newInput.setAttribute('aria-owns', this.listId);
        newInput.setAttribute('aria-haspopup', 'listbox');
        newInput.setAttribute('autocomplete', 'off');
      });
    }
  }

  @Watch('selected')
  @Watch('actvOptEles')
  watchActvOptChange() {
    let c = 0;
    const val = this.connectedInput?.value || '';
    const optIds = [];

    writeTask(() => {
      this.allOptEles.forEach((opt, i) => {
        if (this.actvOptEles.includes(opt)) {
          c++;
          opt.setAttribute('aria-posinset', c + '');
          opt.setAttribute('aria-setsize', this.actvOptEles.length + '');
          opt.hidden = false;
          this.isSelected(opt, val);
        } else {
          opt.removeAttribute('aria-posinset');
          opt.removeAttribute('aria-setsize');
          opt.hidden = true;
          opt.selected = false;
        }
        opt.id = this.listId + '-option-' + i;
        optIds.push(opt.id);
      });
      this.optionIds = optIds;
    });
  }

  @Watch('actvOptEles')
  @Watch('hasNoResult')
  manageCanOpen() {
    if (this.actvOptEles.length || this.hasNoResult) this.canOpen = true;
    else this.canOpen = false;
  }

  /**
   * Opens the dropdown if it can, it should and there are items to display.
   */
  @Watch('shouldOpen')
  @Watch('canOpen')
  manageDropdownDisplay() {
    if (this.shouldOpen && this.canOpen && !this.disabled) this.open = true;
    if (!this.shouldOpen || !this.canOpen) this.open = false;
    this.openWatcher();
  }

  @Watch('actvOptEles')
  fireActiveOptsEvent() {
    this.nanoOptionsUpdated.emit(this.actvOptEles);
  }

  // Events

  /** Fired when an item is selected. */
  @Event() nanoSelect: EventEmitter<HTMLNanoOptionElement>;

  /** Fired when a currently selected item is reselected. */
  @Event() nanoDeselect: EventEmitter<HTMLNanoOptionElement>;

  /** Fired when active options change */
  @Event() nanoOptionsUpdated: EventEmitter<HTMLNanoOptionElement[]>;

  // Private logic

  private isSelected(opt: HTMLNanoOptionElement, val: string) {
    if (val === opt.value || this.selected.includes(opt.value)) {
      opt.selected = true;
    } else opt.selected = false;
  }

  private get dropwdownOpen() {
    if (!this.nanoDropdown || !this.open) return false;
    return true;
  }

  private get exactMatch() {
    return this.allOptEles.find(
      (opt) => this.connectedInput.value === opt.value
    );
  }

  /** reflect value back to the connected input unless the event is cancelled */
  private changeInputValue(opt: HTMLNanoOptionElement) {
    let nanoSelected;

    if (this.selected.includes(opt.value)) {
      // deselect option
      this.selected = this.selected.filter((val) => val !== opt.value);
      nanoSelected = this.nanoDeselect.emit(opt);
    } else {
      // select new option
      this.selected = [...this.selected];
      nanoSelected = this.nanoSelect.emit(opt);
    }

    if (!nanoSelected.defaultPrevented) {
      if (this.connectedInput) this.connectedInput.value = opt.value;

      const event = new window.Event('change');
      this.connectedInput?.dispatchEvent(event);
    }
  }

  // a new option has been picked
  private optSelected = (e: NanoMenuCustomEvent<HTMLNanoOptionElement>) => {
    e.stopPropagation();
    this.changeInputValue(e.detail);
    requestAnimationFrame(() => this.inputChange());

    if (this.type !== 'selctMulti') this.shouldOpen = false;
  };

  // this is for type="select" only.
  // Filter options that start with text - aggregates letters (as opposed to getting the value from input)
  private setOptStartsWith() {
    const attrFind = (toFind: string) =>
      toFind.toLowerCase().substring(0, this.typeToSelect.length) ===
      this.typeToSelect;

    const foundEle = this.allOptEles.find(
      (opt) =>
        opt.value.trim().length > 0 &&
        !opt.disabled &&
        (attrFind(opt.textContent) ||
          attrFind(opt.value) ||
          attrFind(opt.label) ||
          attrFind(opt.filterMeta))
    );
    if (foundEle) this.changeInputValue(foundEle);
  }

  // Event handlers

  private processSlottedContent() {
    requestAnimationFrame(() => {
      this.allOptEles = Array.from(this.host.querySelectorAll('nano-option'));
      this.hasNoResult = !!this.host.querySelector('[slot="no-result"]');

      if (this.connectedInput?.value.length && this.type !== 'select') {
        this.inputChange();
      } else this.actvOptEles = this.allOptEles;
    });
  }

  // sets focus immediately on open when 'select' - mirroring native behaviour
  private handleShow = async () => {
    if (this.shouldFocus) {
      this.shouldFocus = false;
      this.listBox.setFocus();
    } else if (this.type === 'select') this.listBox.showActiveElement();
  };

  private handleHide = () => {
    this.open = false;
    const activeElement = getActiveElement();
    if (activeElement.closest(this.host.tagName.toLowerCase())) {
      this.connectedInput.select();
    }
  };

  // any changes to the connected input value will filter the list of active options
  private inputChange() {
    if (this.disableFilter) {
      this.actvOptEles = this.allOptEles;
      return;
    }

    const val = this.connectedInput.value;
    const valStr = val.trim().toLowerCase();

    let exactMatch = false;
    const activeEles = [];
    const attrFind = (toFind: string) =>
      toFind.toLowerCase().indexOf(valStr) > -1;

    this.allOptEles.forEach((opt) => {
      if (
        (val === opt.value || val === opt.label) &&
        this.type !== 'selctMulti'
      ) {
        opt.selected = true;
        exactMatch = true;
      } else this.isSelected(opt, val);
    });

    this.allOptEles.forEach((opt) => {
      if (!valStr.length || exactMatch) {
        activeEles.push(opt);
      } else if (
        opt.value.trim().length > 0 &&
        !opt.disabled &&
        (attrFind(opt.textContent) ||
          attrFind(opt.value) ||
          attrFind(opt.label) ||
          attrFind(opt.filterMeta))
      ) {
        activeEles.push(opt);
      }
    });

    this.isFiltered = valStr.length && !exactMatch;
    this.actvOptEles = activeEles;
  }

  private inputClick = () => {
    this.shouldOpen = true;
    // open dropdown if possible
    this.manageDropdownDisplay();
  };

  private inputKeydown = (e: KeyboardEvent) => {
    const ignoreKeys = [
      'Shift',
      'ArrowRight',
      'ArrowLeft',
      'Escape',
      'Enter',
      'Tab',
    ];
    if (ignoreKeys.includes(e.key)) {
      if (e.key === 'Tab') this.shouldOpen = false;
      return;
    }

    // When select, mirror native select behaviour. Search on closed list
    if (
      this.type === 'select' &&
      !['ArrowDown', 'ArrowUp'].includes(e.key) &&
      (e.key !== ' ' || this.typeToSelectTimeout)
    ) {
      if (e.key === ' ' || /^[a-z0-9]+$/i.test(e.key)) {
        clearTimeout(this.typeToSelectTimeout);
        this.typeToSelectTimeout = window.setTimeout(() => {
          this.typeToSelect = '';
          this.typeToSelectTimeout = 0;
        }, 750);
        this.typeToSelect += e.key;
        this.setOptStartsWith();
      }
      return;
    }

    this.shouldOpen = true;
    // open dropdown if possible
    this.manageDropdownDisplay();
    if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      this.shouldFocus = true;
    }
  };

  // handles key down on options. Either 'picks'
  // an option or passes event back to input for search / filter
  private optionKeyDown = (
    e: KeyboardEvent & { target: HTMLNanoOptionElement }
  ) => {
    const ignoreKeys = [
      'Shift',
      'ArrowUp',
      'ArrowDown',
      'Escape',
      'Enter',
      'Tab',
      'Space',
      'PageUp',
      'PageDown',
      'Home',
      'End',
      ' ',
    ];
    if (ignoreKeys.includes(e.key)) {
      if (e.key === 'Escape') this.connectedInput.focus();
      return;
    }

    const deleteKeys = ['Delete', 'Backspace'];
    // this passes whatever's typed back to our input control
    if (
      this.type !== 'select' &&
      (!this.exactMatch || deleteKeys.includes(e.key))
    ) {
      this.connectedInput.focus();
    }
  };

  // Component lifecycle

  connectedCallback() {
    this.watchInputChange();
  }

  componentDidLoad() {
    this.manageSlotChangeListener();
    this.openWatcher();
  }

  componentDidRender(): void {
    setTimeout(() => {
      if (!this.connectedInput)
        console.warn(
          'no `nano-input` found. `nano-datalist` should be nested within an `nano-input` or linked via `input` prop',
          this.host
        );
    }, 500);
  }

  render() {
    return (
      <Host
        role={this.actvOptEles.length ? 'listbox' : undefined}
        aria-owns={this.optionIds.length ? this.optionIds.join(' ') : undefined}
        aria-label={
          this.optionIds.length
            ? 'Select options from the list below'
            : undefined
        }
      >
        <nano-dropdown
          {...this.dropDownConfig}
          ref={(el) => (this.nanoDropdown = el)}
          dialogTitle="Select options from the list below"
          class={{
            dlist__dropdown: true,
            'dlist--isfiltered': this.isFiltered,
          }}
          onNanoAfterShow={this.handleShow}
          onNanoAfterHide={this.handleHide}
        >
          <nano-menu
            hidden={!this.actvOptEles.length}
            type="listbox"
            label={this.inputLabel ? this.inputLabel.textContent : undefined}
            class={{
              dlist__menu: true,
              'dlist__menu--open': this.dropwdownOpen,
            }}
            tabIndex={-1}
            onNanoSelect={this.optSelected}
            onKeyDown={this.optionKeyDown}
            ref={(el) => (this.listBox = el)}
          >
            <slot name="list-top" />
            {!this.options.length && <slot />}
            {!!this.options.length && <slot name="internal-opts" />}
            <slot name="list-bottom" />
          </nano-menu>
          <nano-menu
            type="listbox"
            label="No results found"
            hidden={!!this.actvOptEles.length}
            class={{
              dlist__menu: true,
              'dlist__menu--open': this.dropwdownOpen,
            }}
          >
            <slot name="no-result" />
          </nano-menu>
          {!!this.actvOptEles && (
            <div aria-live="polite" role="status" class="dlist__status">
              {this.actvOptEles.length} result
              {this.actvOptEles.length > 1 ? 's' : ''} available.
            </div>
          )}
        </nano-dropdown>
      </Host>
    );
  }
}
